import {Brand, accountKeysStore} from "@mytour/platform";
import type {AcceptCguPayload, CguPolicy, IAccount} from "@mytour/shared";
import {CGU_VERSION_CHANGED} from "@mytour/shared";
import {LetsGo} from "@mytour/ui/form";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import axios from "axios";
import type {ChangeEvent, FormEvent} from "react";
import {useRef, useState} from "react";
import {
  Alert,
  Button,
  Form,
  FormGroup,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Spinner,
} from "reactstrap";
import {CguChangesList} from "./CguChangesList.js";
import {ModalContent, ModalContentWrapper} from "./onboarding-welcome/ModalContent";

export function selectCguChanges(
  acceptedVersion: number | undefined,
  changes: CguPolicy["changes"]
): CguPolicy["changes"] {
  return changes
    .slice()
    .filter((change) => change.version > (acceptedVersion ?? Number.NEGATIVE_INFINITY))
    .sort((a, b) => b.version - a.version);
}

type Props = {
  account: IAccount;
  cguUpdated: boolean;
  policy: CguPolicy;
  disabled?: boolean;
  changed?: boolean;
  onPendingChange?: (pending: boolean) => void;
  onVersionRejected?: (version: number) => void;
};

export function AcceptCGUModal({
  cguUpdated,
  account,
  policy,
  disabled = false,
  changed,
  onPendingChange,
  onVersionRejected,
}: Props) {
  const submitting = useRef(false);
  const {acceptCGU, isAcceptingCGU} = useAcceptCGU();

  const [welcome, setWelcome] = useState<boolean>(!cguUpdated);
  const [cguChecked, setCguChecked] = useState<boolean>(false);
  const [privacyChecked, setPrivacyChecked] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const selectedChanges = selectCguChanges(account.cgu, policy.changes);

  if (welcome) {
    return (
      <Modal isOpen={true} backdrop="static" autoFocus={false} centered>
        <ModalHeader className="mytour-header" close={<></>}>
          <Brand />
        </ModalHeader>
        <ModalBody>
          <ModalContentWrapper>
            <ModalContent>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignContent: "center",
                  height: "100%",
                }}
              >
                <h2 style={{marginTop: "8px"}} className="title-custom-style">
                  {"Première connexion"}
                </h2>
                <p>{"Vous êtes maintenant connecté sur MyTour !"}</p>
              </div>
            </ModalContent>
            <LetsGo onClick={handleWelcomeDone} disabled={false} />
          </ModalContentWrapper>
        </ModalBody>
      </Modal>
    );
  }

  const subtitle = cguUpdated
    ? "Mise à jour de nos conditions d'utilisation"
    : "Merci de consulter nos conditions d'utilisation";
  const introText = !cguUpdated
    ? "En utilisant MyTour, vous acceptez les conditions générales d'utilisation et la politique des données personnelles"
    : `Nouvelle version applicable à partir du ${policy.effectiveDate.split("-").reverse().join("/")}. Merci d'en prendre connaissance et de les accepter.`;
  const canSubmit = cguChecked && privacyChecked;

  return (
    <Modal isOpen={true} backdrop="static" autoFocus={false} centered>
      <ModalHeader className="mytour-header" close={<></>}>
        <Brand />
      </ModalHeader>
      <ModalBody>
        <ModalContentWrapper>
          <ModalContent>
            <p>
              <b>{subtitle}</b>
            </p>
            <p>{introText}</p>
            {cguUpdated && selectedChanges.length > 0 && (
              <CguChangesList changes={selectedChanges} />
            )}
            {changed && (
              <Alert color="info">
                Les conditions ont changé. Merci de prendre connaissance de cette nouvelle version.
              </Alert>
            )}
            {disabled && (
              <Alert color="warning">Connexion nécessaire pour accepter les conditions.</Alert>
            )}
            {error && <Alert color="danger">{error}</Alert>}
            <p>{"J’ai lu et j’accepte :"}</p>
            <Form id="accept-cgu-form" onSubmit={handleSubmit}>
              <FormGroup check style={{paddingLeft: "calc(1em + 12px)"}}>
                <Input
                  type="checkbox"
                  id="accept-cgu-cgu"
                  name="cgu"
                  className="form-check-input"
                  style={{marginLeft: "calc(-1em - 12px)"}}
                  checked={cguChecked}
                  onChange={handleCguChange}
                  aria-labelledby="accept-cgu-cgu-link"
                  disabled={disabled || isAcceptingCGU}
                />
                <a
                  id="accept-cgu-cgu-link"
                  href={policy.termsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  les conditions générales d’utilisation
                </a>
              </FormGroup>
              <FormGroup check style={{paddingLeft: "calc(1em + 12px)"}}>
                <Input
                  type="checkbox"
                  id="accept-cgu-privacy"
                  name="privacy"
                  className="form-check-input"
                  style={{marginLeft: "calc(-1em - 12px)"}}
                  checked={privacyChecked}
                  onChange={handlePrivacyChange}
                  aria-labelledby="accept-cgu-privacy-link"
                  disabled={disabled || isAcceptingCGU}
                />
                <a
                  id="accept-cgu-privacy-link"
                  href={policy.privacyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  la politique de respect des données personnelles
                </a>
              </FormGroup>
            </Form>
          </ModalContent>
          <Button
            color="primary"
            type="submit"
            form="accept-cgu-form"
            disabled={disabled || !canSubmit || isAcceptingCGU}
          >
            {isAcceptingCGU ? <Spinner size="sm" /> : "Valider"}
          </Button>
        </ModalContentWrapper>
      </ModalBody>
    </Modal>
  );

  function handleWelcomeDone() {
    setWelcome(false);
  }

  function handleCguChange(event: ChangeEvent<HTMLInputElement>) {
    setCguChecked(event.target.checked);
  }

  function handlePrivacyChange(event: ChangeEvent<HTMLInputElement>) {
    setPrivacyChecked(event.target.checked);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (disabled || submitting.current || !cguChecked || !privacyChecked) {
      return;
    }
    submitting.current = true;
    onPendingChange?.(true);
    setError(null);
    try {
      await acceptCGU({version: policy.version});
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 409 &&
        error.response.data?.code === CGU_VERSION_CHANGED
      ) {
        onVersionRejected?.(policy.version);
      } else {
        setError("L'enregistrement a échoué, merci de réessayer.");
      }
    } finally {
      submitting.current = false;
      onPendingChange?.(false);
    }
  }
}

function useAcceptCGU() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: acceptCGU,
    networkMode: "always",
    retry: false,
    onSuccess() {
      return queryClient.invalidateQueries({queryKey: accountKeysStore.account()});
    },
  });

  return {
    acceptCGU: mutateAsync,
    isAcceptingCGU: isPending,
  };
}

async function acceptCGU(payload: AcceptCguPayload): Promise<void> {
  await axios.post("api/account/accept-cgu/", payload);
}
